import { useEffect, useState } from "react";
import { useFloating, offset, flip, shift, autoUpdate, Placement, FloatingPortal, hide } from "@floating-ui/react";
import { PortalID } from "frontend/modal/portal-types";
import OnboardingPanel, { OnboardingPanelProps } from "./onboarding-panel";
import styles from "./onboarding-panel.module.css";

interface FloatingOnboardingPanelProps extends OnboardingPanelProps {
  attachTo: string;
  attachToSide?: Placement;
  attachToTargetIndicator?: boolean;
  className?: string;
}

export default function FloatingOnboardingPanel({
  attachTo,
  attachToSide = "bottom",
  attachToTargetIndicator = false,
  onDismiss,
  className,
  ...panelProps
}: FloatingOnboardingPanelProps) {
  const [attachToElement, setAttachToElement] = useState<HTMLElement | null>(null);

  const { refs, floatingStyles, middlewareData } = useFloating({
    open: !!attachToElement,
    placement: attachToSide,
    middleware: [offset(12), flip(), shift({ padding: 8 }), hide()],
    whileElementsMounted: (reference, floating, update) => {
      return autoUpdate(reference, floating, update, {
        ancestorScroll: true,
        ancestorResize: true,
        elementResize: true,
        layoutShift: true,
        animationFrame: true,
      });
    },
  });

  useEffect(() => {
    if (middlewareData.hide?.referenceHidden) {
      setAttachToElement((current) => {
        if (current && attachToTargetIndicator) {
          current.classList.remove(styles.highlightedAttachedElement);
        }
        return null;
      });
      onDismiss?.();
    }
  }, [middlewareData.hide, attachToTargetIndicator]);

  useEffect(() => {
    if (!attachTo) {
      setAttachToElement(null);
      return;
    }

    const initialElement = document.querySelector(attachTo) as HTMLElement;
    if (initialElement) {
      if (attachToTargetIndicator) {
        initialElement.classList.add(styles.highlightedAttachedElement);
      }
      setAttachToElement(initialElement);
      return () => {
        const element = document.querySelector(attachTo) as HTMLElement;
        if (element && attachToTargetIndicator) {
          element.classList.remove(styles.highlightedAttachedElement);
        }
      };
    }

    const observer = new MutationObserver(() => {
      const element = document.querySelector(attachTo) as HTMLElement;
      if (element) {
        if (attachToTargetIndicator) {
          element.classList.add(styles.highlightedAttachedElement);
        }
        setAttachToElement(element);
        observer.disconnect();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
      const element = document.querySelector(attachTo) as HTMLElement;
      if (element && attachToTargetIndicator) {
        element.classList.remove(styles.highlightedAttachedElement);
      }
    };
  }, [attachTo, attachToTargetIndicator]);

  useEffect(() => {
    if (attachToElement) {
      refs.setReference(attachToElement);
    } else {
      refs.setReference(null);
    }
  }, [attachToElement, refs]);

  if (!attachToElement) {
    return null;
  }

  return (
    <FloatingPortal id={PortalID.Floaters}>
      <div
        ref={refs.setFloating}
        style={{
          ...floatingStyles,
          visibility: middlewareData.hide?.referenceHidden ? "hidden" : "visible",
        }}
        className={className}
      >
        <OnboardingPanel {...panelProps} centered={false} onDismiss={onDismiss} />
      </div>
    </FloatingPortal>
  );
}
